import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Box,
  Typography,
  Chip,
  useTheme
} from '@mui/material';
import { useTranslation } from '../utils/translations';

const DataTable = ({ stateData, onStateClick, selectedState, language }) => {
  const t = useTranslation(language);
  const theme = useTheme();
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('state_TrumpJA');


  if (!stateData || stateData.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t('noDataAvailable')}
      </Typography>
    );
  }

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };



  const handleRowClick = (stateCode) => {
    onStateClick(stateCode);
  };

  // Sort data
  const sortedData = [...stateData].sort((a, b) => {
    if (orderBy === 'state') {
      return order === 'asc' ? a.state.localeCompare(b.state) : b.state.localeCompare(a.state);
    }
    const aValue = a[orderBy] || 0;
    const bValue = b[orderBy] || 0;
    return order === 'asc' ? aValue - bValue : bValue - aValue;
  });

  // Use all sorted data (no pagination)
  const displayData = sortedData;

  const getApprovalChip = (approval) => {
    const percentage = approval * 100; // Convert to percentage
    let color = 'default';
    if (percentage >= 70) color = 'error';
    else if (percentage >= 50) color = 'warning';
    else if (percentage >= 30) color = 'info';
    else color = 'success';

    return (
      <Chip
        label={`${percentage.toFixed(1)}%`}
        size="small"
        color={color}
        variant="outlined"
      />
    );
  };

  const getDisapprovalChip = (disapproval) => {
    const percentage = (disapproval || 0) * 100; // Convert to percentage
    let color = 'default';
    if (percentage >= 70) color = 'error';
    else if (percentage >= 50) color = 'warning';
    else if (percentage >= 30) color = 'info';
    else color = 'success';

    return (
      <Chip
        label={`${percentage.toFixed(1)}%`}
        size="small"
        color={color}
        variant="outlined"
      />
    );
  };

  const getNoOpinionChip = (noOpinion) => {
    const percentage = (noOpinion || 0) * 100; // Convert to percentage
    
    return (
      <Chip
        label={`${percentage.toFixed(1)}%`}
        size="small"
        color="default"
        variant="outlined"
      />
    );
  };

  const headCells = [
    { id: 'state', numeric: false, label: t('state') },
    { id: 'state_TrumpJA', numeric: true, label: t('trumpApproval') },
    { id: 'state_TrumpDisapproval', numeric: true, label: t('trumpDisapproval') },
    { id: 'state_TrumpNoOpinion', numeric: true, label: t('trumpNoOpinion') }
  ];

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <TableContainer sx={{ 
        flex: 1,
        overflowY: 'auto', 
        overflowX: 'hidden',
        width: '100%'
      }}>
        <Table size="small" aria-label="state data table" sx={{ tableLayout: 'fixed', width: '100%' }}>
          <TableHead         sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          backgroundColor: theme.palette.mode === 'dark' ? '#414141' : '#ffffff',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '1px',
              backgroundColor: theme.palette.divider,
            }
          }}>
            <TableRow>
              {headCells.map((headCell, index) => (
                <TableCell
                  key={headCell.id}
                  align={headCell.numeric ? 'right' : 'left'}
                  sortDirection={orderBy === headCell.id ? order : false}
                  sx={{
                    width: '25%',
                    padding: '8px 4px',
                    fontSize: '0.85rem',
                    backgroundColor: theme.palette.mode === 'dark' ? '#414141' : '#ffffff',
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    fontWeight: 600,
                    color: theme.palette.mode === 'dark' ? '#ffffff' : '#000000',
                    minWidth: '60px'
                  }}
                >
                  <TableSortLabel
                    active={orderBy === headCell.id}
                    direction={orderBy === headCell.id ? order : 'asc'}
                    onClick={() => handleRequestSort(headCell.id)}
                    sx={{ 
                      fontSize: '0.85rem',
                      color: theme.palette.mode === 'dark' ? '#ffffff' : '#000000',
                      '& .MuiTableSortLabel-icon': {
                        color: `${theme.palette.mode === 'dark' ? '#ffffff' : '#000000'} !important`
                      },
                      '&:hover': {
                        color: theme.palette.mode === 'dark' ? '#ffffff' : '#000000'
                      },
                      '&.Mui-active': {
                        color: theme.palette.mode === 'dark' ? '#ffffff' : '#000000'
                      }
                    }}
                  >
                    {headCell.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {displayData.map((row) => (
              <TableRow
                key={row.state}
                hover
                onClick={() => handleRowClick(row.state)}
                sx={{
                  cursor: 'pointer',
                  backgroundColor: selectedState === row.state ? 'action.selected' : 'inherit',
                  '&:hover': {
                    backgroundColor: 'action.hover'
                  }
                }}
              >
                <TableCell 
                  component="th" 
                  scope="row" 
                  sx={{ 
                    width: '25%', 
                    padding: '6px 4px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  <Typography variant="body2" fontWeight="medium" sx={{ 
                    fontFamily: '"Poppins", sans-serif',
                    fontSize: '0.8rem'
                  }}>
                    {row.state}
                  </Typography>
                </TableCell>
                <TableCell 
                  align="right" 
                  sx={{ 
                    width: '25%', 
                    padding: '6px 4px'
                  }}
                >
                  {getApprovalChip(row.state_TrumpJA)}
                </TableCell>
                <TableCell 
                  align="right" 
                  sx={{ 
                    width: '25%', 
                    padding: '6px 4px'
                  }}
                >
                  {getDisapprovalChip(row.state_TrumpDisapproval)}
                </TableCell>
                <TableCell 
                  align="right" 
                  sx={{ 
                    width: '25%', 
                    padding: '6px 4px'
                  }}
                >
                  {getNoOpinionChip(row.state_TrumpNoOpinion)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

    </Box>
  );
};

export default DataTable; 